"use client";

import { useCallback, useState } from "react";
import type {
  MspDocumentCategory,
  MspDocumentDifficulty,
} from "../components/msp-document-schema";

export type MspDocAiAssistMode = "improve" | "expand" | "summarize";

interface DraftArgs {
  title?: string;
  category?: MspDocumentCategory;
  difficulty?: MspDocumentDifficulty;
  instructions?: string;
}

interface AssistArgs {
  mode: MspDocAiAssistMode;
  content: string;
  instructions?: string;
}

export interface MspDocAiDraft {
  content: string;
  excerpt?: string;
  tags?: string[];
}

async function postDraft(body: unknown): Promise<MspDocAiDraft> {
  const response = await fetch("/api/ai/msp-doc-draft", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = (await response.json().catch(() => null)) as
    | (MspDocAiDraft & { error?: string })
    | null;
  if (!response.ok || !json) {
    throw new Error(json?.error || "AI request failed. Please try again.");
  }
  return { content: json.content, excerpt: json.excerpt, tags: json.tags };
}

/**
 * Client access to the MSP-doc AI writer. Tracks its own loading/error state so
 * callers can drive buttons/spinners without wiring TanStack Query for a
 * one-shot, non-cached generation call.
 */
export function useMspDocAi() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | null> => {
    setIsGenerating(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : "AI request failed");
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const generateDraft = useCallback(
    (args: DraftArgs) => run(() => postDraft({ mode: "draft", ...args })),
    [run]
  );

  const assist = useCallback(
    ({ mode, content, instructions }: AssistArgs) =>
      run(async () => {
        const res = await postDraft({ mode, content, instructions });
        return res.content;
      }),
    [run]
  );

  return { generateDraft, assist, isGenerating, error, clearError: () => setError(null) };
}
